/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 26/04/22
 */

import {
    composeWithUi,
    getData,
    JsonFormsState,
    mapStateToLayoutProps,
    OwnPropsOfLayout,
    Resolve,
} from '@jsonforms/core';
import { typedObjectKeys } from '@/wpt-lib/utils/helpers';
import {
    DataReplacementElement,
    DataReplacementProps,
} from '@/forms-renderers/chakra-ui/additional/DataReplacement/ChakraDataReplacementRenderer';
import MathEvaluator from '@/forms-renderers/chakra-ui/additional/DataReplacement/MathEvaluator';
import { cloneDeep } from 'lodash';

type OwnPropsOfDataReplacement = OwnPropsOfLayout;

/**
 * Map JSONForms state to data replacement props.
 * @param state JSONForms state tree
 * @param ownProps any own props
 * @returns {DataReplacementProps}
 */
export const mapStateToDataReplacementProps = (
    state: JsonFormsState,
    ownProps: OwnPropsOfDataReplacement,
): DataReplacementProps => {
    const layoutProps = mapStateToLayoutProps(state, ownProps);

    const dataReplacementElement = cloneDeep(ownProps.uischema) as DataReplacementElement;

    const config = cloneDeep(state.jsonforms.config);
    const metadata = config.metadata;
    // Get data for each scope item
    const rootData = getData(state);
    const paths = (dataReplacementElement.scopes || []).map((scope) => composeWithUi({ scope }, ownProps.path || ''));
    const scopeData = paths.map((path) => Resolve.data(rootData, path));
    const scopeMetaData = paths.map((path) => metadata[path]);

    const replaceData = (v: string) => {
        return scopeData.reduce<string>((text, data, i) => {
            return text.replaceAll('${' + i + '}', (data ?? '').toString());
        }, `${v}`);
    };

    const replaceExpressions = (v: string) => {
        const expressions = dataReplacementElement.expressions || [];
        return expressions.reduce<string>((text, expression, i) => {
            try {
                return text.replaceAll('%{' + i + '}', MathEvaluator.evaluate(expression).toString());
            } catch (e) {
                return text.replaceAll('%{' + i + '}', '');
            }
        }, `${v}`);
    };

    const replaceWithMetaData = (v: string) => {
        return scopeMetaData.reduce<string>((text, data, i) => {
            return text.replaceAll('@{' + i + '}', (data ?? '').toString());
        }, `${v}`);
    };

    const replaceStringValuesWith = (obj: Record<string, unknown>, replaceFunc: (val: string) => string) => {
        typedObjectKeys(obj).map((key) => {
            const item = obj[key];
            if (Array.isArray(item)) {
                obj[key] = item.map((v) => {
                    if (typeof v === 'string') {
                        return replaceFunc(v);
                    }
                    return v;
                });
            } else if (typeof item === 'string') {
                obj[key] = replaceFunc(item);
            }
        });
    };

    const replaceInObj = (obj: Record<string, unknown>) => {
        replaceStringValuesWith(obj, replaceData);
        replaceStringValuesWith(obj, replaceWithMetaData);
        replaceStringValuesWith(obj, replaceExpressions);
    };

    replaceInObj(dataReplacementElement as Record<keyof DataReplacementElement, unknown>);
    if (dataReplacementElement.options) {
        replaceInObj(dataReplacementElement.options);
    }

    return {
        ...layoutProps,
        uischema: dataReplacementElement,
    };
};
